// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Bootstrap + wiring. Connects the live camera loop and the file-scan path to the
 * motion DSP, the gate, the event store and the UI. Owns no DSP itself.
 */

import './styles/main.css';
import {
  buildShell, renderGallery, renderSegments, revokeSegUrls, openModal, closeModal, isOverlayOpen,
  toast, howItWorksBody, privacyBody, aboutBody, playerModalBody, type SegView,
} from './ui';
import { initGlossary } from './glossary';
import { mountEventDrawer, emit } from './eventlog';
import {
  computeLumaGrid, diffGrids, sensitivityToParams, maskFromRect, GRID_COLS, GRID_ROWS,
} from './motion';
import { MotionGate, segmentsFromScores, formatTimecode, buildTimelineCSV, buildSegmentsJSON } from './gate';
import { startCamera, stopStream, allTracksEnded } from './camera';
import { ClipRecorder, pickClipMime, recordingSupported } from './recorder';
import { saveEvent, loadEvents, deleteEvent, clearEvents } from './store';
import { acquireWakeLock, releaseWakeLock, reacquireIfWanted } from './wakelock';
import { beep, requestNotifications, notifyMotion, notificationPermission } from './notify';
import { scanVideo, captureFrameAt, whenReady } from './filescan';
import {
  saveBlob, downloadText, renderContactSheetPNG, canShareFiles, shareFile, type SheetItem,
} from './deliver';
import type { CaptureEvent, NormRect, Segment } from './types';

const root = document.getElementById('app');
if (!root) throw new Error('missing #app');
const refs = buildShell(root);
initGlossary();

// ── Tunables ─────────────────────────────────────────────────────────────────
const TICK_MS = 120;
const MIN_ACTIVE_FRAMES = 3;
const COOLDOWN_MS = 2500;
const MAX_CLIP_MS = 30_000;
const DETECT_W = 160;
const DETECT_H = 120;
const METER_MAX = 0.35;

const FILE_STEP = 0.2;
const FILE_MIN_ACTIVE = 2;
const FILE_COOLDOWN = 0.6;
const FILE_MIN_DURATION = 0.2;

// ── Shared state ─────────────────────────────────────────────────────────────
let sensitivity = 55;
let currentZone: NormRect | null = null;
let currentMask = maskFromRect(null, GRID_COLS, GRID_ROWS);
let beepOn = false;
let notifyOn = false;
let micOn = false;
let facing: 'user' | 'environment' = 'user';
let events: CaptureEvent[] = [];

// ── Live detection ───────────────────────────────────────────────────────────
const detectCanvas = document.createElement('canvas');
detectCanvas.width = DETECT_W;
detectCanvas.height = DETECT_H;
const detectCtx = detectCanvas.getContext('2d', { willReadFrequently: true })!;

let stream: MediaStream | null = null;
let armed = false;
let loopTimer = 0;
let prevGrid: Float32Array | null = null;
let lastActive: Uint8Array | null = null;
const gate = new MotionGate({ minActiveFrames: MIN_ACTIVE_FRAMES, cooldownMs: COOLDOWN_MS, maxClipMs: MAX_CLIP_MS });

let recorder: ClipRecorder | null = null;
let clipMime = '';
let eventStartTs = 0;
let eventPeak = 0;
let snapshotPromise: Promise<Blob> | null = null;

function grabGrid(): Float32Array | null {
  const w = refs.video.videoWidth;
  const h = refs.video.videoHeight;
  if (!w || !h) return null;
  detectCtx.drawImage(refs.video, 0, 0, DETECT_W, DETECT_H);
  const data = detectCtx.getImageData(0, 0, DETECT_W, DETECT_H).data;
  return computeLumaGrid(data, DETECT_W, DETECT_H, GRID_COLS, GRID_ROWS);
}

function captureSnapshot(): Promise<Blob> {
  const vw = refs.video.videoWidth || DETECT_W;
  const vh = refs.video.videoHeight || DETECT_H;
  const scale = Math.min(1, 640 / vw);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d')!.drawImage(refs.video, 0, 0, w, h);
  return new Promise<Blob>((resolve, reject) => {
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('snapshot failed'))), 'image/jpeg', 0.82);
  });
}

function tick(): void {
  if (!stream) return;
  const grid = grabGrid();
  if (!grid) return;
  const { cellDelta, triggerFraction } = sensitivityToParams(sensitivity);
  let score = 0;
  if (prevGrid) {
    const diff = diffGrids(prevGrid, grid, cellDelta, currentMask);
    score = diff.score;
    lastActive = diff.activeCells;
  }
  prevGrid = grid;
  updateMeter(score, triggerFraction);
  drawOverlay();

  if (!armed) return;
  const now = Date.now();
  const over = score >= triggerFraction;
  if (over && score > eventPeak) eventPeak = score;
  const tr = gate.push(over, now);
  if (tr === 'start') startEvent();
  else if (tr === 'stop') void stopEvent();
}

function startEvent(): void {
  eventStartTs = Date.now();
  eventPeak = Math.max(eventPeak, sensitivityToParams(sensitivity).triggerFraction);
  snapshotPromise = captureSnapshot().catch(() => new Blob());
  recorder = stream && recordingSupported() ? new ClipRecorder(stream, clipMime) : null;
  setRecordingUI(true);
  if (beepOn) beep();
  if (notifyOn) notifyMotion(`Movement detected at ${new Date().toLocaleTimeString()}`);
  emit('motion', 'warn', 'Motion detected — capturing');
}

async function stopEvent(): Promise<void> {
  const rec = recorder;
  const startTs = eventStartTs;
  const snapP = snapshotPromise;
  const peak = eventPeak;
  recorder = null;
  snapshotPromise = null;
  eventPeak = 0;
  setRecordingUI(false);

  const durationMs = Date.now() - startTs;
  let snapshot: Blob;
  try {
    snapshot = snapP ? await snapP : await captureSnapshot();
  } catch {
    snapshot = new Blob();
  }
  let clip: Blob | null = null;
  let clipType = '';
  if (rec) {
    try {
      clip = await rec.stop();
      clipType = rec.mime;
    } catch {
      clip = null;
    }
  }
  const ev: CaptureEvent = {
    id: `ev-${startTs}-${Math.round(peak * 1000)}`,
    ts: startTs,
    durationMs,
    peakScore: peak,
    snapshot,
    clip,
    clipType,
    hasAudio: micOn,
  };
  events.push(ev);
  await saveEvent(ev);
  renderGalleryNow();
  emit('capture', 'ok', `Event saved · ${(durationMs / 1000).toFixed(1)}s${clip ? '' : ' (photo only)'}`);
}

function startLoop(): void {
  stopLoop();
  loopTimer = window.setInterval(tick, TICK_MS);
}
function stopLoop(): void {
  if (loopTimer) window.clearInterval(loopTimer);
  loopTimer = 0;
}

async function arm(): Promise<void> {
  if (armed) return;
  refs.camStatus.textContent = 'starting camera…';
  try {
    stream = await startCamera(refs.video, { facing, audio: micOn });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    emit('camera', 'err', `Camera unavailable: ${message}`);
    refs.camStatus.textContent = 'No camera — scan a video instead';
    toast('Camera unavailable — switch to “Scan a video” to analyse a file', 'err');
    switchMode('file');
    return;
  }
  clipMime = pickClipMime();
  armed = true;
  gate.reset();
  prevGrid = null;
  eventPeak = 0;
  refs.camOverlay.hidden = true;
  refs.micToggle.disabled = true;
  setArmUI(true);
  sizeOverlay();
  startLoop();
  const wl = await acquireWakeLock();
  emit('camera', 'ok', `Armed — watching for motion${wl ? ' · screen kept awake' : ''}. Frames are compared locally, never uploaded.`);
  if (!recordingSupported()) toast('Clip recording is not supported here — events save a photo only', 'err');
}

function disarm(reason: string): void {
  if (!armed && !stream) return;
  if (recorder) void stopEvent();
  stopLoop();
  const s = stream;
  stopStream(s);
  refs.video.srcObject = null;
  stream = null;
  armed = false;
  prevGrid = null;
  lastActive = null;
  refs.micToggle.disabled = false;
  setArmUI(false);
  setRecordingUI(false);
  refs.camOverlay.hidden = false;
  clearOverlay();
  void releaseWakeLock();
  emit('camera', 'info', `Disarmed (${reason}) · tracks ended: ${allTracksEnded(s)}`);
}

async function reArm(): Promise<void> {
  if (!armed) return;
  stopLoop();
  stopStream(stream);
  refs.video.srcObject = null;
  stream = null;
  try {
    stream = await startCamera(refs.video, { facing, audio: micOn });
    prevGrid = null;
    startLoop();
    sizeOverlay();
  } catch {
    disarm('camera lost');
  }
}

// ── Live UI helpers ──────────────────────────────────────────────────────────

function setArmUI(on: boolean): void {
  refs.armBtn.innerHTML = on
    ? `<svg class="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v10"/><path d="M18.4 6.6a9 9 0 1 1-12.77.04"/></svg> Disarm`
    : `<svg class="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v10"/><path d="M18.4 6.6a9 9 0 1 1-12.77.04"/></svg> Arm camera`;
  refs.armBtn.classList.toggle('armed', on);
  const chip = refs.statusChip;
  chip.dataset.state = on ? 'armed' : 'idle';
  (chip.querySelector('.chip-label') as HTMLElement).textContent = on ? 'Armed' : 'Idle';
  if (on) refs.camStatus.textContent = '';
}

function setRecordingUI(on: boolean): void {
  refs.recDot.hidden = !on;
  const chip = refs.statusChip;
  if (on) {
    chip.dataset.state = 'recording';
    (chip.querySelector('.chip-label') as HTMLElement).textContent = 'Recording';
  } else if (armed) {
    chip.dataset.state = 'armed';
    (chip.querySelector('.chip-label') as HTMLElement).textContent = 'Armed';
  }
}

function updateMeter(score: number, triggerFraction: number): void {
  const pct = Math.min(100, (score / METER_MAX) * 100);
  refs.meterFill.style.width = `${pct}%`;
  refs.meterThresh.style.left = `${Math.min(100, (triggerFraction / METER_MAX) * 100)}%`;
  refs.meterVal.textContent = `${Math.round(score * 100)}%`;
  refs.meterFill.classList.toggle('over', score >= triggerFraction);
}

// ── Overlay: detection zone + active cells ───────────────────────────────────

function sizeOverlay(): void {
  const rect = refs.video.getBoundingClientRect();
  refs.overlay.width = Math.max(1, Math.round(rect.width));
  refs.overlay.height = Math.max(1, Math.round(rect.height));
}
function clearOverlay(): void {
  const ctx = refs.overlay.getContext('2d');
  if (ctx) ctx.clearRect(0, 0, refs.overlay.width, refs.overlay.height);
}
function drawOverlay(): void {
  const ctx = refs.overlay.getContext('2d');
  if (!ctx) return;
  const W = refs.overlay.width;
  const H = refs.overlay.height;
  ctx.clearRect(0, 0, W, H);

  // active cells heat (only inside zone, only while armed)
  if (armed && lastActive) {
    const cw = W / GRID_COLS;
    const ch = H / GRID_ROWS;
    ctx.fillStyle = 'rgba(56, 224, 208, 0.28)';
    for (let i = 0; i < lastActive.length; i++) {
      if (!lastActive[i] || !currentMask[i]) continue;
      const gx = i % GRID_COLS;
      const gy = (i / GRID_COLS) | 0;
      ctx.fillRect(gx * cw, gy * ch, cw + 0.5, ch + 0.5);
    }
  }

  // detection zone rectangle
  if (currentZone) {
    ctx.strokeStyle = '#38e0d0';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(currentZone.x * W, currentZone.y * H, currentZone.w * W, currentZone.h * H);
    ctx.setLineDash([]);
  }
}

// ── Zone drawing (pointer) ───────────────────────────────────────────────────

let zoneDrawing = false;
let zoneStart: { x: number; y: number } | null = null;

function overlayNorm(e: PointerEvent): { x: number; y: number } {
  const rect = refs.overlay.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
    y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
  };
}

refs.overlay.addEventListener('pointerdown', (e) => {
  if (!zoneDrawing) return;
  e.preventDefault();
  refs.overlay.setPointerCapture(e.pointerId);
  zoneStart = overlayNorm(e);
});
refs.overlay.addEventListener('pointermove', (e) => {
  if (!zoneDrawing || !zoneStart) return;
  const p = overlayNorm(e);
  currentZone = normRect(zoneStart, p);
  drawOverlay();
});
refs.overlay.addEventListener('pointerup', (e) => {
  if (!zoneDrawing || !zoneStart) return;
  const p = overlayNorm(e);
  const rect = normRect(zoneStart, p);
  zoneStart = null;
  if (rect.w < 0.03 || rect.h < 0.03) {
    currentZone = null;
  } else {
    currentZone = rect;
  }
  applyZone();
  setZoneDrawing(false);
});

function normRect(a: { x: number; y: number }, b: { x: number; y: number }): NormRect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}
function applyZone(): void {
  currentMask = maskFromRect(currentZone, GRID_COLS, GRID_ROWS);
  refs.clearZoneBtn.hidden = !currentZone;
  emit('motion', 'info', currentZone ? 'Detection zone set' : 'Detection zone cleared — watching the whole frame');
  drawOverlay();
}
function setZoneDrawing(on: boolean): void {
  zoneDrawing = on;
  refs.zoneToggle.setAttribute('aria-pressed', String(on));
  refs.overlay.classList.toggle('drawing', on);
}

// ── Gallery wiring ───────────────────────────────────────────────────────────

function renderGalleryNow(): void {
  renderGallery(refs, events, canShareFiles([new File([new Blob()], 'x.webm', { type: 'video/webm' })]), {
    onPlay: (ev, url) => {
      if (url) openModal('Event clip', playerModalBody(url, ''));
      else {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(ev.snapshot);
        img.style.width = '100%';
        img.style.borderRadius = '10px';
        openModal('Event snapshot', img);
      }
    },
    onDownloadClip: (ev) => {
      if (!ev.clip) return;
      const ext = ev.clipType.includes('mp4') ? 'mp4' : 'webm';
      void guardSave(saveBlob(ev.clip, `sentrywell-clip-${stamp(ev.ts)}.${ext}`), 'Clip');
    },
    onDownloadSnap: (ev) => void guardSave(saveBlob(ev.snapshot, `sentrywell-photo-${stamp(ev.ts)}.jpg`), 'Photo'),
    onShare: async (ev) => {
      const src = ev.clip ?? ev.snapshot;
      const ext = ev.clip ? (ev.clipType.includes('mp4') ? 'mp4' : 'webm') : 'jpg';
      const file = new File([src], `sentrywell-${stamp(ev.ts)}.${ext}`, { type: src.type || 'application/octet-stream' });
      if (!canShareFiles([file])) {
        toast('Sharing files is not supported here', 'err');
        return;
      }
      await shareFile(file, 'Sentrywell event');
    },
    onDelete: async (ev) => {
      events = events.filter((e) => e.id !== ev.id);
      await deleteEvent(ev.id);
      renderGalleryNow();
      emit('capture', 'info', 'Event deleted');
    },
  });
}

async function guardSave(p: Promise<'saved' | 'downloaded'>, label: string): Promise<void> {
  try {
    const r = await p;
    toast(r === 'saved' ? `${label} saved` : `${label} downloaded`);
  } catch (e) {
    if (!(e instanceof DOMException && e.name === 'AbortError')) toast('Download failed', 'err');
  }
}

function stamp(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

// ── File scan ────────────────────────────────────────────────────────────────

let previewUrl = '';
let lastSamples: Array<{ t: number; score: number }> = [];
let lastSegViews: SegView[] = [];
let lastSourceName = 'video';
let scanAbort = { aborted: false };

async function loadVideoFile(file: File): Promise<void> {
  if (!file.type.startsWith('video/') && !/\.(mp4|webm|mov|m4v|ogg)$/i.test(file.name)) {
    toast('That is not a video file', 'err');
    return;
  }
  lastSourceName = file.name;
  emit('scan', 'info', `Opening ${file.name} (${Math.round(file.size / 1024)} KB) — never uploaded`);
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = URL.createObjectURL(file);
  refs.previewVideo.src = previewUrl;
  refs.fileStage.hidden = false;
  refs.dropEmpty.hidden = true;
  refs.dropzone.classList.add('has-video');
  try {
    await whenReady(refs.previewVideo);
    await runScan();
  } catch (err) {
    emit('scan', 'err', `Could not decode: ${err instanceof Error ? err.message : String(err)}`);
    toast('Could not open that video', 'err');
  }
}

async function runScan(): Promise<void> {
  scanAbort.aborted = true;
  scanAbort = { aborted: false };
  const signal = scanAbort;
  refs.scanWrap.hidden = false;
  refs.scanStatus.textContent = 'Scanning for motion…';
  refs.scanBar.style.width = '0%';
  const { cellDelta, triggerFraction } = sensitivityToParams(sensitivity);
  emit('scan', 'info', `Scanning at sensitivity ${sensitivity}`);
  let result: { samples: Array<{ t: number; score: number }>; duration: number };
  try {
    result = await scanVideo(refs.previewVideo, {
      stepSec: FILE_STEP,
      cellDelta,
      mask: null,
      signal,
      onProgress: (r) => {
        refs.scanBar.style.width = `${Math.round(r * 100)}%`;
      },
    });
  } catch (err) {
    refs.scanStatus.textContent = 'Scan failed';
    emit('scan', 'err', `Scan failed: ${err instanceof Error ? err.message : String(err)}`);
    return;
  }
  if (signal.aborted) return;
  lastSamples = result.samples;
  const segs = segmentsFromScores(result.samples, {
    triggerFraction,
    minActiveFrames: FILE_MIN_ACTIVE,
    cooldown: FILE_COOLDOWN,
    minDuration: FILE_MIN_DURATION,
  });
  await buildSegViews(segs);
  refs.scanStatus.textContent = `Found ${segs.length} motion ${segs.length === 1 ? 'event' : 'events'} in ${result.duration.toFixed(1)}s`;
  refs.scanBar.style.width = '100%';
  emit('scan', 'ok', `${segs.length} motion events found`);
}

async function buildSegViews(segs: Segment[]): Promise<void> {
  revokeSegUrls();
  const views: SegView[] = [];
  for (const seg of segs) {
    let thumb: Blob;
    try {
      thumb = await captureFrameAt(refs.previewVideo, seg.peakT);
    } catch {
      thumb = new Blob();
    }
    views.push({ seg, thumb });
  }
  lastSegViews = views;
  renderSegments(refs, views, {
    onJump: (t) => {
      refs.previewVideo.currentTime = t;
      void refs.previewVideo.play().catch(() => {});
    },
    onDownloadSnap: (v) =>
      void guardSave(saveBlob(v.thumb, `sentrywell-${lastSourceName.replace(/\.[^.]+$/, '')}-${formatTimecode(v.seg.peakT).replace(':', 'm')}.jpg`), 'Snapshot'),
  });
}

// ── Wiring: mode tabs ────────────────────────────────────────────────────────

function switchMode(mode: 'live' | 'file'): void {
  refs.modeTabs.querySelectorAll<HTMLButtonElement>('.mode-tab').forEach((t) =>
    t.classList.toggle('active', t.dataset.mode === mode),
  );
  refs.livePanel.hidden = mode !== 'live';
  refs.filePanel.hidden = mode !== 'file';
  if (mode !== 'live') disarm('switched to file mode');
}
refs.modeTabs.querySelectorAll<HTMLButtonElement>('.mode-tab').forEach((tab) => {
  tab.addEventListener('click', () => switchMode(tab.dataset.mode as 'live' | 'file'));
});

// ── Wiring: live controls ────────────────────────────────────────────────────

refs.armBtn.addEventListener('click', () => (armed ? disarm('user') : void arm()));
refs.armBig.addEventListener('click', () => void arm());

refs.sensSlider.addEventListener('input', () => {
  sensitivity = Number(refs.sensSlider.value);
  refs.sensLabel.textContent = String(sensitivity);
});

refs.beepToggle.addEventListener('click', () => {
  beepOn = !beepOn;
  refs.beepToggle.classList.toggle('on', beepOn);
  refs.beepToggle.setAttribute('aria-pressed', String(beepOn));
  if (beepOn) beep();
});
refs.notifyToggle.addEventListener('click', async () => {
  if (!notifyOn) {
    const ok = await requestNotifications();
    notifyOn = ok;
    if (!ok) toast('Notifications were blocked — enable them in your browser to use this', 'err');
  } else {
    notifyOn = false;
  }
  refs.notifyToggle.classList.toggle('on', notifyOn);
  refs.notifyToggle.setAttribute('aria-pressed', String(notifyOn));
});
refs.micToggle.addEventListener('click', () => {
  if (refs.micToggle.disabled) return;
  micOn = !micOn;
  refs.micToggle.classList.toggle('on', micOn);
  refs.micToggle.setAttribute('aria-pressed', String(micOn));
});
refs.zoneToggle.addEventListener('click', () => setZoneDrawing(!zoneDrawing));
refs.clearZoneBtn.addEventListener('click', () => {
  currentZone = null;
  applyZone();
});
refs.flipBtn.addEventListener('click', () => {
  facing = facing === 'user' ? 'environment' : 'user';
  void reArm();
});

refs.clearAllBtn.addEventListener('click', async () => {
  events = [];
  await clearEvents();
  renderGalleryNow();
  emit('capture', 'info', 'All events cleared from this device');
});
refs.sheetBtn.addEventListener('click', () => void exportContactSheet(events.map((e) => ({ blob: e.snapshot, label: new Date(e.ts).toLocaleTimeString() })), 'Sentrywell — captured events'));

// ── Wiring: file controls ────────────────────────────────────────────────────

refs.sampleBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  void loadSample();
});
refs.dropzone.addEventListener('click', (e) => {
  if ((e.target as HTMLElement).closest('#file-stage')) return;
  refs.fileInput.click();
});
refs.dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    refs.fileInput.click();
  }
});
refs.fileInput.addEventListener('change', () => {
  const file = refs.fileInput.files?.[0];
  if (file) void loadVideoFile(file);
  refs.fileInput.value = '';
});
refs.dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  refs.dropzone.classList.add('drag');
});
refs.dropzone.addEventListener('dragleave', () => refs.dropzone.classList.remove('drag'));
refs.dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  refs.dropzone.classList.remove('drag');
  const file = e.dataTransfer?.files?.[0];
  if (file) void loadVideoFile(file);
});
refs.rescanBtn.addEventListener('click', () => void runScan());
refs.csvBtn.addEventListener('click', () =>
  void guardSave(downloadText(buildTimelineCSV(lastSamples), 'sentrywell-timeline.csv', 'text/csv'), 'Timeline CSV'),
);
refs.jsonBtn.addEventListener('click', () =>
  void guardSave(downloadText(buildSegmentsJSON(lastSegViews.map((v) => v.seg), lastSourceName), 'sentrywell-segments.json', 'application/json'), 'Segments JSON'),
);
refs.fileSheetBtn.addEventListener('click', () =>
  void exportContactSheet(lastSegViews.map((v) => ({ blob: v.thumb, label: formatTimecode(v.seg.peakT) })), `Sentrywell — ${lastSourceName}`),
);

async function loadSample(): Promise<void> {
  try {
    emit('scan', 'info', 'Loading sample clip');
    const res = await fetch('samples/sample.mp4');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    await loadVideoFile(new File([blob], 'sample.mp4', { type: blob.type || 'video/mp4' }));
  } catch (err) {
    emit('scan', 'err', `Sample failed: ${err instanceof Error ? err.message : String(err)}`);
    toast('Could not load the sample', 'err');
  }
}

async function exportContactSheet(items: SheetItem[], title: string): Promise<void> {
  if (!items.length) {
    toast('Nothing to put on a contact sheet yet', 'err');
    return;
  }
  try {
    const png = await renderContactSheetPNG(items, title);
    await guardSave(saveBlob(png, 'sentrywell-contact-sheet.png'), 'Contact sheet');
  } catch {
    toast('Could not build the contact sheet', 'err');
  }
}

// ── Modals + drawer ──────────────────────────────────────────────────────────

const modalBodies: Record<string, () => HTMLElement | string> = {
  how: howItWorksBody,
  privacy: privacyBody,
  about: aboutBody,
};
const modalTitles: Record<string, string> = {
  how: 'How it works',
  privacy: 'Privacy & threat model',
  about: 'About Sentrywell',
};
document.querySelectorAll<HTMLElement>('[data-modal]').forEach((el) => {
  el.addEventListener('click', () => {
    const key = el.dataset.modal!;
    const build = modalBodies[key];
    if (build) openModal(modalTitles[key], build());
  });
});

let drawerDispose: (() => void) | null = null;
function openDrawer(): void {
  refs.drawer.hidden = false;
  refs.drawerToggle.setAttribute('aria-expanded', 'true');
  if (!drawerDispose) drawerDispose = mountEventDrawer(refs.drawer, closeDrawer);
}
function closeDrawer(): void {
  refs.drawer.hidden = true;
  refs.drawerToggle.setAttribute('aria-expanded', 'false');
}
refs.drawerToggle.addEventListener('click', () => (refs.drawer.hidden ? openDrawer() : closeDrawer()));

// ── Keyboard ─────────────────────────────────────────────────────────────────

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if (isOverlayOpen()) {
      closeModal();
      closeDrawer();
    }
    if (zoneDrawing) setZoneDrawing(false);
    return;
  }
  if (e.key === ' ' && !refs.livePanel.hidden && !isOverlayOpen()) {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.tagName === 'VIDEO') return;
    e.preventDefault();
    armed ? disarm('keyboard') : void arm();
  }
});

// ── Lifecycle: keep monitoring while armed; always release on unload ──────────

window.addEventListener('resize', () => {
  if (stream) sizeOverlay();
});
document.addEventListener('visibilitychange', () => void reacquireIfWanted());
window.addEventListener('pagehide', () => disarm('page hidden'));

// ── Init ─────────────────────────────────────────────────────────────────────

if (notificationPermission() === 'granted') {
  notifyOn = true;
  refs.notifyToggle.classList.add('on');
  refs.notifyToggle.setAttribute('aria-pressed', 'true');
}

void (async () => {
  events = await loadEvents();
  renderGalleryNow();
  if (events.length) emit('system', 'info', `${events.length} saved event(s) restored from this device`);
})();

emit('system', 'ok', 'Sentrywell ready — zero backend, nothing uploaded');

// ── Test hook (the harness can't wave at a camera) ───────────────────────────

interface TestHook {
  sensParams: (s: number) => { cellDelta: number; triggerFraction: number };
  gridScore: (a: number[], b: number[], cellDelta: number) => number;
  simulateGate: (overs: boolean[], stepMs?: number) => Array<[number, string]>;
  segCount: (samples: Array<{ t: number; score: number }>, opts: { triggerFraction: number; minActiveFrames: number; cooldown: number; minDuration: number }) => number;
  lastScan: () => { segments: number; samples: number };
  lastScores: () => number[];
  captureSynthetic: () => Promise<number>;
  eventCount: () => number;
}
(window as unknown as { __sentrywell: TestHook }).__sentrywell = {
  sensParams: (s) => sensitivityToParams(s),
  gridScore: (a, b, cellDelta) => diffGrids(Float32Array.from(a), Float32Array.from(b), cellDelta).score,
  simulateGate: (overs, stepMs = TICK_MS) => {
    const g = new MotionGate({ minActiveFrames: MIN_ACTIVE_FRAMES, cooldownMs: COOLDOWN_MS, maxClipMs: MAX_CLIP_MS });
    const out: Array<[number, string]> = [];
    overs.forEach((o, i) => {
      const tr = g.push(o, i * stepMs);
      if (tr) out.push([i, tr]);
    });
    return out;
  },
  segCount: (samples, opts) => segmentsFromScores(samples, opts).length,
  lastScan: () => ({ segments: lastSegViews.length, samples: lastSamples.length }),
  lastScores: () => lastSamples.map((s) => Number(s.score.toFixed(3))),
  async captureSynthetic() {
    const c = document.createElement('canvas');
    c.width = 320;
    c.height = 240;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#20242c';
    ctx.fillRect(0, 0, 320, 240);
    ctx.fillStyle = '#e8e8ea';
    ctx.fillRect(120, 70, 54, 120);
    const blob = await new Promise<Blob>((res) => c.toBlob((b) => res(b ?? new Blob()), 'image/jpeg', 0.8));
    const ts = Date.now();
    const ev: CaptureEvent = {
      id: `synthetic-${ts}`,
      ts,
      durationMs: 1500,
      peakScore: 0.42,
      snapshot: blob,
      clip: null,
      clipType: '',
      hasAudio: false,
    };
    events.push(ev);
    await saveEvent(ev);
    renderGalleryNow();
    return events.length;
  },
  eventCount: () => events.length,
};
